import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { SiteHeader, SiteFooter, useMeta } from "@/components/site/Chrome";

export default function Login() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useMeta("Sign in — ExamRay", "Sign in to ExamRay with a one-time email code. No passwords.");

  const requestCode = async () => {
    if (!email.includes("@")) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/request-code", { email: email.trim() });
      setStage("code");
      toast.success("Code sent — check your email");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const verify = async () => {
    if (code.trim().length < 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: email.trim(), code: code.trim() });
      await refresh();
      toast.success("Signed in");
      navigate(data.is_admin ? "/admin" : "/dashboard");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="er" data-testid="login-page">
      <SiteHeader />
      <section className="er-capture" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", maxWidth: 1040 }}>
        <div>
          <p className="er-kicker">SIGN IN</p>
          <h1 className="er-h1" style={{ fontSize: 42, lineHeight: 1.07 }}>No passwords. One code.</h1>
          <p style={{ marginTop: 18, fontSize: 16.5, lineHeight: 1.6, color: "var(--ink-3)", maxWidth: "30em" }}>Enter the email you bought with, or the one you used for your free exam. We send a six-digit code that signs you straight in.</p>
          <div className="er-rows" style={{ marginTop: 30 }}>
            {[["Enter your email", "The one on your purchase receipt or free-exam link."], ["Open the code", "Six digits, valid for ten minutes, one use."], ["You are in", "Your exams, scores and review mode, exactly where you left them."]].map(([t, d], i) => (
              <div key={t} className="er-row er-steprow"><span className="n">0{i + 1}</span>
                <div><p style={{ fontWeight: 600, fontSize: 15.5 }}>{t}</p><p style={{ marginTop: 5, fontSize: 14, lineHeight: 1.6, color: "var(--ink-4)" }}>{d}</p></div></div>
            ))}
          </div>
        </div>
        <div className="er-buybox" data-testid="login-card">
          <div className="er-rule" />
          <div style={{ padding: "28px 26px" }}>
            {stage === "email" ? (
              <>
                <p className="er-label" style={{ color: "var(--ink-4)" }}>STEP 1 OF 2</p>
                <p className="er-h2" style={{ marginTop: 14 }}>Where should we send the code?</p>
                <label className="er-field-label" style={{ marginTop: 20 }}>EMAIL ADDRESS
                  <input data-testid="login-email-input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && requestCode()} placeholder="you@company.com" className="er-input" /></label>
                <button data-testid="login-request-code-btn" onClick={requestCode} disabled={busy} className="er-btn" style={{ marginTop: 18, width: "100%", padding: "14px 0", fontSize: 15 }}>Email me a code</button>
                <p style={{ marginTop: 14, fontSize: 13, lineHeight: 1.55, color: "var(--ink-5)" }}>New here? <Link to="/free" data-testid="login-free-link">Start with a free exam</Link> — no account needed.</p>
              </>
            ) : (
              <>
                <p className="er-label" style={{ color: "var(--ink-4)" }}>STEP 2 OF 2</p>
                <p className="er-h2" style={{ marginTop: 14 }}>Check your inbox.</p>
                <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6, color: "var(--ink-4)" }}>We sent a code to <span className="er-mono" style={{ fontSize: 13.5, color: "var(--ink)" }} data-testid="login-sent-email">{email.trim()}</span>. Nothing arrived? Check spam, then resend.</p>
                <label className="er-field-label" style={{ marginTop: 20 }}>SIX-DIGIT CODE
                  <input data-testid="login-code-input" autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         onKeyDown={(e) => e.key === "Enter" && verify()} maxLength={6} placeholder="••••••"
                         className="er-input mono" style={{ textAlign: "center", fontSize: 24, letterSpacing: "0.45em", padding: "12px 0 12px 0.45em" }} /></label>
                <button data-testid="login-verify-btn" onClick={verify} disabled={busy} className="er-btn" style={{ marginTop: 18, width: "100%", padding: "14px 0", fontSize: 15 }}>Sign in</button>
                <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13.5 }}>
                  <button data-testid="login-back-btn" onClick={() => { setStage("email"); setCode(""); }} className="er-link-btn" style={{ color: "var(--ink-4)" }}>Use a different email</button>
                  <button data-testid="login-resend-btn" onClick={requestCode} disabled={busy} className="er-link-btn">Resend code</button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
